import { useState } from 'react';
import { createBlog } from '../services/adminService';
import { serverTimestamp } from 'firebase/firestore';

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');

const BLOGS = [
  {
    title: 'Complete Bridal Makeup Guide for Indian Weddings 2025',
    category: 'Bridal',
    excerpt: 'Everything an Indian bride needs to know about bridal makeup — from skin prep and look selection to questions to ask your makeup artist before the big day.',
    seoTitle: 'Complete Bridal Makeup Guide for Indian Weddings 2025 | Atelier by Shriji',
    seoDescription: 'A complete bridal makeup guide for Indian brides in 2025. Learn how to choose the right look, prep your skin, and work with your makeup artist for a flawless wedding day.',
    content: `<h2>Why Bridal Makeup is Different from Everyday Makeup</h2>
<p>Your wedding day is photographed, filmed, and remembered for decades. Bridal makeup must perform flawlessly across hours, shifting light conditions, tears of joy, and the relentless flash of cameras. Unlike everyday makeup, it blends artistry with endurance — and choosing the right artist and the right look can make all the difference.</p>

<h2>Step 1 — Start with Your Skin, Not Your Look</h2>
<p>The most common mistake brides make is focusing on the makeup look before addressing their skin. Great bridal makeup starts three to six months before the wedding with a consistent skincare routine. Begin with:</p>
<ul>
<li><strong>Hydration:</strong> Drink at least 2–3 litres of water daily. Hydrated skin holds foundation better and catches light beautifully.</li>
<li><strong>SPF daily:</strong> Sun damage creates uneven texture that no primer can fully correct.</li>
<li><strong>A professional facial regimen:</strong> Work with a dermatologist or aesthetician to address pigmentation, acne scars, or dullness at least 8 weeks before the wedding.</li>
<li><strong>Avoid new products in the final 2 weeks:</strong> Skin can react unpredictably — stick to what you know works.</li>
</ul>

<h2>Step 2 — Choose the Right Bridal Makeup Style</h2>
<p>Indian bridal looks range from heavily traditional to soft and contemporary. Here are the most requested styles in 2025:</p>
<ul>
<li><strong>Classic Traditional:</strong> Deep smoky eyes, bold reds or berry lips, heavy contouring. Perfect for large, traditional ceremonies with rich lehenga colours.</li>
<li><strong>Soft Glam:</strong> Dewy skin, champagne-toned eye shadow, a nude-to-rose lip. Flatters modern brides who want elegance without heaviness.</li>
<li><strong>Airbrush Bridal:</strong> Uses compressed air to apply foundation — results in an incredibly smooth, long-wearing finish ideal for outdoor ceremonies and summer weddings.</li>
<li><strong>Minimalist Bridal:</strong> Skin-first approach with subtle definition. Growing rapidly in popularity among contemporary brides.</li>
</ul>
<blockquote>The best bridal look is the one that makes you feel like the most beautiful version of yourself — not a character you are playing for a day.</blockquote>

<h2>Step 3 — Booking Your Makeup Artist</h2>
<p>Book your bridal makeup artist at least 6–9 months before your wedding, especially during peak season (October–February in North India). During your initial consultation, ask:</p>
<ul>
<li>Can I see a full portfolio of bridal work — not just highlight reels?</li>
<li>How long does bridal makeup typically take in your chair?</li>
<li>Do you do a trial session, and is it included in the package?</li>
<li>What is your policy if you are unwell on the wedding day?</li>
<li>Can you accommodate my full bridal party (bridesmaids, mother of the bride)?</li>
</ul>

<h2>Step 4 — The Bridal Trial</h2>
<p>A bridal trial is non-negotiable. Schedule it 4–6 weeks before the wedding, wearing your actual bridal jewellery if possible. Take photographs in natural and artificial light, and test longevity by wearing the look for 6–8 hours. Note what you love and what you would change — a good makeup artist welcomes this feedback.</p>

<h2>Step 5 — Day-Of Preparation</h2>
<p>On the morning of your wedding, set yourself up for success:</p>
<ul>
<li>Sleep well the night before — dark circles and puffiness are real challenges for a makeup artist to correct.</li>
<li>Moisturise immediately after your bath, at least 20 minutes before makeup application begins.</li>
<li>Eat a full meal before sitting in the chair — foundation applied on an empty stomach often appears paler due to lower blood circulation.</li>
<li>Arrive with a clean, product-free face — no SPF, moisturiser, or any other product unless your artist has requested otherwise.</li>
</ul>

<h2>What to Expect from Atelier by Shriji</h2>
<p>At Atelier by Shriji, every bride receives a personalised consultation, a dedicated trial session, and a look crafted to complement her outfit, skin tone, and personality. We use professional-grade products designed to last through the longest of ceremonies, and our team has worked with brides across traditional, fusion, and destination weddings across India.</p>
<p>Ready to start planning your bridal look? <strong>Contact us to schedule your consultation.</strong></p>`,
  },
  {
    title: 'How to Choose the Right Makeup Artist for Your Wedding Day',
    category: 'Bridal',
    excerpt: 'Choosing a wedding makeup artist is one of the most important decisions a bride makes. Here is exactly what to look for, what to ask, and what red flags to avoid.',
    seoTitle: 'How to Choose a Wedding Makeup Artist in India — Complete Guide',
    seoDescription: 'Not sure how to choose the right makeup artist for your wedding? This guide covers portfolios, trials, pricing, contracts, and red flags every Indian bride should know.',
    content: `<h2>Why This Decision Matters More Than Most</h2>
<p>Your bridal makeup artist will be with you for some of the most emotionally significant hours of your life. They will be in your getting-ready suite, working close to your face, and their work will appear in every photograph you display in your home for decades. The stakes are real — and so is the difference between an artist who is right for you and one who is not.</p>

<h2>Start with Style Alignment</h2>
<p>Every makeup artist has a visual signature. Some specialise in dramatic, editorial looks; others in soft, skin-forward bridal. Before reaching out, look at an artist's last 20–30 bridal posts — not just the ones they tag as "best of." Ask yourself:</p>
<ul>
<li>Does the makeup look consistently beautiful across different skin tones, not just lighter ones?</li>
<li>Does the skin look like skin, or does it look like a mask?</li>
<li>Do the eyes, lips, and overall look feel cohesive, or are they competing for attention?</li>
<li>Do the brides look like polished versions of themselves, or do they all look similar?</li>
</ul>

<h2>What to Ask During Your Consultation</h2>
<p>A consultation is your interview of the artist as much as theirs of you. Cover these areas:</p>
<ul>
<li><strong>Experience with your skin tone:</strong> Ask to see portfolio examples on similar skin tones to yours.</li>
<li><strong>Products used:</strong> Professional artists use professional-grade brands. Ask about the specific foundation and setting products they use — these directly affect longevity.</li>
<li><strong>Backup plan:</strong> What happens if the artist is ill on your wedding day? Is there a trained associate who can step in?</li>
<li><strong>Timeline:</strong> How long does bridal makeup take? How much time per bridesmaid? This affects your entire morning schedule.</li>
<li><strong>Travel and accommodation:</strong> If your venue is outside the city, clarify who covers transport and whether there is an outstation fee.</li>
</ul>

<h2>Understanding Pricing</h2>
<p>Bridal makeup pricing in India varies widely — from ₹8,000 to ₹1,50,000 or more depending on the artist's experience, location, and package scope. A few things to understand:</p>
<ul>
<li>Low pricing is not always a bargain. Inexperienced artists often undercut the market and cannot deliver under pressure.</li>
<li>High pricing is not always a guarantee. Always evaluate work over reputation alone.</li>
<li>Ask for a clear breakdown: what is included (trial, touch-up kit, duration), what costs extra, and what the cancellation policy is.</li>
</ul>
<blockquote>The cost of a bad makeup artist on your wedding day is not the fee you paid — it is the photographs you will look at for the rest of your life.</blockquote>

<h2>Red Flags to Watch For</h2>
<p>These are warning signs that an artist may not be the right fit:</p>
<ul>
<li>Reluctance to do a trial session or charging disproportionately for it.</li>
<li>No written contract or unclear cancellation terms.</li>
<li>Dismissiveness during consultation — if they do not listen now, they will not listen on the day.</li>
<li>Heavy reliance on filters in portfolio photographs — request unedited images or video.</li>
<li>Inability to name the products they use or vague answers about product brands.</li>
</ul>

<h2>The Trial Is Your Safety Net</h2>
<p>A trial session is the single most effective way to evaluate an artist before committing. During the trial, assess:</p>
<ul>
<li>Does the artist listen carefully and incorporate your references?</li>
<li>Is the skin preparation thorough (cleansing, priming, colour correction)?</li>
<li>Does the makeup look as good after 6 hours as it did at the start?</li>
<li>Did you feel comfortable and at ease throughout?</li>
</ul>
<p>If the trial does not feel right, you have the right to look elsewhere — no matter how far along the booking is.</p>

<h2>Why Atelier by Shriji Brides Book Early</h2>
<p>At Atelier by Shriji, we limit the number of bridal bookings we take each season to give every bride our full attention. Our process begins with a detailed consultation, includes a comprehensive trial, and ends with a wedding-day experience that feels calm, unhurried, and entirely focused on you. Our dates for the 2025 season are filling — reach out early to avoid disappointment.</p>`,
  },
  {
    title: 'Pre-Bridal Skincare Routine: 3 Months Before Your Wedding',
    category: 'Skincare',
    excerpt: 'A month-by-month pre-bridal skincare guide that will give you glowing, makeup-ready skin on your wedding day — no last-minute panic required.',
    seoTitle: 'Pre-Bridal Skincare Routine: 3-Month Timeline for Glowing Wedding Skin',
    seoDescription: 'Start your pre-bridal skincare routine 3 months before your wedding with this step-by-step guide covering facials, home care, diet, and what to avoid before the big day.',
    content: `<h2>Why 3 Months Is the Magic Number</h2>
<p>Skin cells turn over roughly every 28 days. Three months gives you three full cycles to address concerns like pigmentation, texture, dullness, and acne — transforming your skin at a cellular level rather than just masking problems on the surface. Starting your pre-bridal skincare routine 90 days out is the single best investment you can make in how your wedding photographs look.</p>

<h2>Month 3 (12 Weeks Out): Foundation Building</h2>
<p>This is your diagnostic and establishment phase. Begin with:</p>
<ul>
<li><strong>Dermatologist visit:</strong> If you have active acne, pigmentation, or recurring skin issues, see a professional now. Treatments like chemical peels, retinol prescriptions, or targeted serums need time to work and time to settle before the wedding.</li>
<li><strong>Establish a basic routine:</strong> Cleanser, vitamin C serum (morning), moisturiser, SPF 30+ (morning), retinol or a gentle exfoliant (night, 2–3x per week).</li>
<li><strong>Hydration:</strong> Begin tracking your water intake. Aim for 2.5–3 litres per day.</li>
<li><strong>Nutrition:</strong> Add foods rich in Omega-3 (walnuts, flaxseed, fish) and antioxidants (berries, leafy greens). These directly support skin barrier function.</li>
</ul>

<h2>Month 2 (8 Weeks Out): Active Treatment</h2>
<p>Your skin has stabilised on its new routine. Now it is time to treat specific concerns.</p>
<ul>
<li><strong>Professional facial:</strong> Schedule a deep cleansing or brightening facial this month. Options include hydrafacials, oxygen facials, or a customised treatment from a trusted aesthetician.</li>
<li><strong>Target pigmentation:</strong> Add a niacinamide serum (5–10%) if dark spots or uneven tone are a concern. This ingredient is well-tolerated and visibly effective within 4–6 weeks.</li>
<li><strong>Eye care:</strong> Begin using an eye cream consistently — look for ingredients like peptides or caffeine to address fine lines and puffiness.</li>
<li><strong>Body skin:</strong> Do not neglect the rest of you — exfoliate 2x per week and moisturise daily, focusing on arms, neck, and décolletage (visible in bridal outfits).</li>
</ul>
<blockquote>Great bridal makeup does not create beautiful skin — it reveals it. The work you do in these three months is what your makeup artist will build upon.</blockquote>

<h2>Month 1 (4 Weeks Out): Refinement and Caution</h2>
<p>The final month is about refinement — and knowing what NOT to do.</p>
<ul>
<li><strong>Final professional facial:</strong> Schedule a gentle hydrating or glow facial 2–3 weeks before the wedding. Avoid any aggressive treatments (deep chemical peels, microneedling, laser) within 3 weeks of the date — skin needs recovery time.</li>
<li><strong>Stop introducing new products:</strong> Your skin has now adapted to your routine. Any new product risks a reaction. This is not the time for experiments.</li>
<li><strong>Sleep:</strong> Begin prioritising 7–8 hours of sleep consistently. Sleep deprivation shows directly on skin — pallor, puffiness, and dull texture.</li>
<li><strong>Stress management:</strong> Cortisol (the stress hormone) triggers sebum overproduction and inflammation. Yoga, meditation, or even a 30-minute walk daily makes a measurable difference to your skin.</li>
</ul>

<h2>Final Week: Calm and Maintain</h2>
<ul>
<li>Cleanse, tone, and moisturise as usual — no deviations.</li>
<li>Avoid alcohol in the 48 hours before your wedding (it is deeply dehydrating).</li>
<li>Do not wax or thread eyebrows within 48 hours — skin needs time to settle after hair removal.</li>
<li>Get a full night of sleep the night before. If nerves make this difficult, a short meditation or a familiar sleep routine helps.</li>
<li>Eat a nourishing meal the morning of — your skin colour literally changes when your blood sugar drops.</li>
</ul>

<h2>Products Worth Investing In</h2>
<p>You do not need an elaborate routine — you need the right routine. These categories matter most:</p>
<ul>
<li>A gentle, non-stripping cleanser</li>
<li>Vitamin C serum (ascorbic acid or sodium ascorbyl phosphate)</li>
<li>Niacinamide serum if pigmentation is a concern</li>
<li>A good moisturiser with ceramides or hyaluronic acid</li>
<li>SPF 30–50 every single morning, regardless of weather</li>
</ul>
<p>At Atelier by Shriji, we recommend pre-bridal skincare consultations as part of our bridal packages — because the best makeup we can do is on skin that has been carefully prepared. Ask about our pre-bridal skincare guidance when booking your consultation.</p>`,
  },
  {
    title: 'Airbrush vs Traditional Bridal Makeup: Which is Right for You?',
    category: 'Makeup Tips',
    excerpt: 'Airbrush or traditional brush-and-sponge? Both techniques have real advantages for Indian brides. Here is how to decide which will serve you best on your wedding day.',
    seoTitle: 'Airbrush vs Traditional Bridal Makeup: Which is Better for Indian Brides?',
    seoDescription: 'Comparing airbrush and traditional bridal makeup for Indian weddings. Learn the pros, cons, and which technique suits your skin type, budget, and ceremony duration.',
    content: `<h2>What Is Airbrush Makeup?</h2>
<p>Airbrush makeup uses a small compressor to spray a fine mist of foundation and other products onto the skin, building coverage in ultra-thin layers. The result is a flawless, second-skin finish that is extraordinarily difficult to achieve with brushes alone. It has been the standard for film and high-end editorial work for decades, and has become increasingly popular for Indian weddings — particularly outdoor ceremonies and summer/monsoon weddings.</p>

<h2>What Is Traditional Bridal Makeup?</h2>
<p>Traditional makeup applies products using brushes, sponges, and fingers. In the hands of a skilled artist, this technique offers tremendous versatility — it can create anything from dewy, soft-glam looks to heavily contoured, dramatic bridal eyes. It is the method used by the vast majority of professional makeup artists and works beautifully across all skin types when applied correctly.</p>

<h2>Airbrush: Pros and Cons</h2>
<p><strong>Advantages:</strong></p>
<ul>
<li>Extremely long-lasting — silicone-based airbrush formulas can last 12–16 hours with minimal touch-ups.</li>
<li>Transfer-resistant — holds up to perspiration, humidity, and tears far better than most traditional formulas.</li>
<li>Lightweight feel — many brides describe it as barely feeling like they are wearing anything.</li>
<li>Ideal for outdoor and destination weddings where heat and humidity are factors.</li>
<li>Photographs exceptionally well, especially in natural light.</li>
</ul>
<p><strong>Limitations:</strong></p>
<ul>
<li>Difficult to adjust mid-day — if an area needs touching up, matching the airbrush formula requires the equipment to be on hand.</li>
<li>Less forgiving on very dry or textured skin — dehydration can cause patchy application.</li>
<li>The flawless finish can look slightly flat in certain lighting if not layered with traditional techniques.</li>
<li>Requires a specialist — not all makeup artists are trained in airbrush technique.</li>
</ul>

<h2>Traditional Makeup: Pros and Cons</h2>
<p><strong>Advantages:</strong></p>
<ul>
<li>Deeply versatile — an expert artist can achieve any finish from natural to full glam.</li>
<li>Easier to touch up during the day using products you can carry in a small kit.</li>
<li>More adaptable to different skin types, textures, and concerns in real time.</li>
<li>Can create depth, dimension, and warmth that is harder to achieve with airbrush alone.</li>
</ul>
<p><strong>Limitations:</strong></p>
<ul>
<li>Longevity depends heavily on the specific products used and the setting techniques applied.</li>
<li>More susceptible to transfer and humidity if not properly sealed.</li>
</ul>
<blockquote>The question is not which technique is better — it is which technique is better for you, your wedding environment, and your artist's expertise.</blockquote>

<h2>Which Should You Choose?</h2>
<p>Here is a simple framework:</p>
<ul>
<li><strong>Choose airbrush if:</strong> Your wedding is outdoors, during summer or monsoon, involves significant physical activity (garba, sangeet dancing), or you have oily skin that tends to melt makeup through the day.</li>
<li><strong>Choose traditional if:</strong> You have dry or mature skin, prefer a warmer and more dimensional look, value easy DIY touch-ups, or your artist has demonstrated exceptional longevity with traditional products.</li>
<li><strong>Consider a hybrid approach:</strong> Many advanced artists — including at Atelier by Shriji — use airbrush for base coverage and traditional techniques for eyes, contour, and lips. This combines the longevity of airbrush with the depth and dimension of hand application.</li>
</ul>

<h2>How to Evaluate at Your Trial</h2>
<p>If you are unsure which technique to book, ask your artist to do a hybrid trial — airbrush base, traditional eyes and lips. Wear it for a full day, including any outdoor time, and assess how it holds. Pay attention to:</p>
<ul>
<li>How does the skin look at hour 8 versus hour 1?</li>
<li>Does it transfer on fabric (check your collar or a white cloth)?</li>
<li>Does it feel comfortable across different temperatures?</li>
</ul>
<p>At Atelier by Shriji, we offer both airbrush and traditional bridal techniques — and we will recommend the right approach for your skin and your wedding based on your trial consultation. Book your bridal trial to experience the difference for yourself.</p>`,
  },
  {
    title: 'Professional Makeup Courses: A Complete Career Guide for Aspiring Artists',
    category: 'Courses',
    excerpt: 'Thinking of becoming a professional makeup artist? This guide covers everything — from choosing the right course to building your portfolio and landing your first clients.',
    seoTitle: 'Professional Makeup Courses India: Complete Career Guide for Aspiring Artists',
    seoDescription: 'A complete guide to professional makeup courses in India — what to look for in a course, how to build a portfolio, and how to start your makeup artist career with confidence.',
    content: `<h2>Is a Career in Makeup Right for You?</h2>
<p>The professional makeup industry in India has grown dramatically over the last decade. With the rise of destination weddings, digital content creation, film and OTT productions, and a booming beauty market, trained makeup artists are in genuine demand. But it is also a deeply competitive field — one where skill, work ethic, and business sense all matter equally. Before investing in training, ask yourself honestly:</p>
<ul>
<li>Do I have an innate interest in faces, skin, and beauty — not just the glamour of the industry?</li>
<li>Am I comfortable working early mornings, late nights, and weekends (the peak hours of this industry)?</li>
<li>Do I have the patience for client management, repetition, and continuous learning?</li>
</ul>
<p>If the answers are yes, a career in professional makeup can be deeply fulfilling — creative, social, financially rewarding, and entirely self-directed.</p>

<h2>Types of Makeup Courses in India</h2>
<p>The Indian market offers a range of training options, each with different scopes, durations, and outcomes:</p>
<ul>
<li><strong>Short Certificate Courses (2–4 weeks):</strong> Cover fundamentals — skin prep, corrective techniques, basic bridal and party looks. Good for beginners who want to explore before committing to a longer programme.</li>
<li><strong>Diploma Programmes (3–6 months):</strong> Comprehensive training across bridal, editorial, airbrush, hair, and often includes business modules. The most common path for those seeking professional careers.</li>
<li><strong>Advanced Masterclasses:</strong> Specific skill deep-dives (e.g., airbrush, HD makeup, editorial looks). Designed for working artists who want to expand their repertoire.</li>
<li><strong>Online Courses:</strong> Growing in quality and convenience. Best for theory and technique understanding, though hands-on practice still requires in-person training.</li>
</ul>
<blockquote>The certificate from a makeup course is worth far less than the skill you develop during it. Choose based on the quality of teaching, not the prestige of the certificate.</blockquote>

<h2>What to Look for in a Makeup Course</h2>
<p>With hundreds of institutes and independent artists offering training, the quality varies enormously. Evaluate any programme on these criteria:</p>
<ul>
<li><strong>Instructor credentials:</strong> Has the trainer worked professionally in the field — on brides, editorial shoots, films? Look for a working portfolio, not just teaching experience.</li>
<li><strong>Hands-on practice time:</strong> The ratio of theory to practical application matters. A course where students spend more time watching than doing will not build real skill.</li>
<li><strong>Product training:</strong> Are you being trained on professional-grade products (MAC, Anastasia Beverly Hills, Kryolan, RCMA) or drugstore products? The transition to professional kits is far easier when you have trained with them.</li>
<li><strong>Business guidance:</strong> Does the course teach pricing, client management, contracts, and portfolio building? The business of makeup is as important as the art.</li>
<li><strong>Ongoing mentorship:</strong> The best institutes offer access to instructors after the course ends — for guidance on early client situations and career questions.</li>
</ul>

<h2>Building Your Portfolio</h2>
<p>Your portfolio is your most powerful marketing tool. Start building it during your course and continue aggressively after:</p>
<ul>
<li><strong>Photograph everything:</strong> Every practice model, every course assignment, every favour you do for a friend's event. Consistency compounds over time.</li>
<li><strong>Invest in one good shoot:</strong> Collaborate with a photographer, stylist, and model for a dedicated editorial shoot. Even one strong set of images can transform how potential clients perceive you.</li>
<li><strong>Show range:</strong> Include bridal, party, editorial, and natural looks. Clients want to see that you can adapt.</li>
<li><strong>Prioritise quality over quantity:</strong> Ten exceptional photographs beat fifty mediocre ones. Curate ruthlessly.</li>
</ul>

<h2>Your First Clients</h2>
<p>Getting your first paid clients is the hardest step — and the one most aspiring artists overthink. Practical approaches that work:</p>
<ul>
<li>Offer discounted trial rates to build portfolio and testimonials (not free — charging something, even modestly, sets a professional tone).</li>
<li>Reach out to local event photographers and offer to collaborate on shoots — this builds both portfolio and referral networks.</li>
<li>Document everything on Instagram — clients increasingly discover makeup artists through social media before any other channel.</li>
<li>Reach out to small wedding venues, photographers, and event planners to be added to their preferred vendor lists.</li>
</ul>

<h2>About Our Makeup Courses at Atelier by Shriji</h2>
<p>Our makeup courses are designed for students who are serious about the craft. Led directly by Shriji Agarwal, the curriculum covers bridal, party, airbrush, editorial, and skin-first techniques — alongside practical business training that most institutes ignore. Our small batch sizes ensure every student receives personalised attention throughout the programme.</p>
<p>Whether you are a complete beginner or a working artist looking to level up, our courses are structured to meet you where you are. <strong>Enquire about upcoming batches and course details through the link below.</strong></p>`,
  },
];

export default function SeedBlogs() {
  const [status, setStatus] = useState(BLOGS.map(() => 'idle'));
  const [done, setDone] = useState(false);

  const seedAll = async () => {
    const newStatus = [...status];
    for (let i = 0; i < BLOGS.length; i++) {
      newStatus[i] = 'running';
      setStatus([...newStatus]);
      try {
        const blog = BLOGS[i];
        await createBlog({
          title: blog.title,
          slug: slugify(blog.title),
          category: blog.category,
          excerpt: blog.excerpt,
          content: blog.content,
          seoTitle: blog.seoTitle,
          seoDescription: blog.seoDescription,
          coverImage: '',
          published: true,
          publishedAt: serverTimestamp(),
        });
        newStatus[i] = 'done';
      } catch (err) {
        newStatus[i] = 'error: ' + err.message;
      }
      setStatus([...newStatus]);
    }
    setDone(true);
  };

  const colors = { idle: '#888', running: '#b87333', done: '#2e7d32', };
  const isRunning = status.some(s => s === 'running');

  return (
    <div style={{ maxWidth: 700, margin: '60px auto', padding: '0 20px', fontFamily: 'inherit' }}>
      <h1 style={{ color: '#3e1c00', fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', marginBottom: 8 }}>Seed Blog Posts</h1>
      <p style={{ color: '#666', marginBottom: 32 }}>This will publish 5 SEO-optimised blog posts to Firestore. Run once only.</p>

      {BLOGS.map((b, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #eee' }}>
          <span style={{ width: 16, height: 16, borderRadius: '50%', background: colors[status[i]] || '#e53935', display: 'inline-block', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, color: '#3e1c00', fontSize: 14 }}>{b.title}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{status[i]}</div>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 28 }}>
        {!done ? (
          <button
            onClick={seedAll}
            disabled={isRunning}
            style={{ background: '#3e1c00', color: '#eae1d8', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 15, fontWeight: 700, cursor: isRunning ? 'not-allowed' : 'pointer' }}
          >
            {isRunning ? 'Seeding…' : 'Seed All 5 Blogs'}
          </button>
        ) : (
          <div>
            <p style={{ color: '#2e7d32', fontWeight: 700, marginBottom: 12 }}>All blogs seeded! Visit <a href="/blog" style={{ color: '#b87333' }}>/blog</a> to see them live.</p>
            <p style={{ color: '#888', fontSize: 13 }}>You can now delete this page or remove the route from App.jsx.</p>
          </div>
        )}
      </div>
    </div>
  );
}
